// Gemeinsame Restlaufzeit-Logik für die „Restzeit"-Anzeige.
// Wird sowohl in /reports (Reports-Tabelle) als auch in /customers
// (Vorgangs-Liste) verwendet, damit beide identisch rechnen und aussehen.

export type RemainingColor = 'neutral' | 'red' | 'orange' | 'yellow' | 'green';

export interface RemainingTime {
  text: string;
  color: RemainingColor;
}

// Status, die im BAFA-Sinn als „abgeschlossen/grün" gelten (Auszahlung erfolgt).
const GREEN_STATUSES = ['Abgeschlossen', 'Ausgezahlt', 'Zahlung erfolgt'];

export function isGreenStatus(status?: string): boolean {
  return !!status && GREEN_STATUSES.includes(status);
}

// Verbleibende Zeit bis zum Ende des Beratungszeitraums (Bericht-Enddatum).
// Stufen: bis 28 Tage in Tagen (rot), bis 90 Tage in Monaten (orange),
// darüber in Monaten (grün). Abgeschlossen → „Erledigt", abgelaufen → „Abgelaufen".
export function calculateRemainingTime(endDate: string, status?: string): RemainingTime {
  const today = new Date();
  const end = new Date(endDate);
  const diffDays = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (isGreenStatus(status)) {
    return { text: 'Erledigt', color: 'green' };
  }
  if (diffDays <= 0) {
    return { text: 'Abgelaufen', color: 'red' };
  }
  if (diffDays <= 28) {
    return { text: `${diffDays} ${diffDays === 1 ? 'Tag' : 'Tage'}`, color: 'red' };
  }
  const months = Math.round(diffDays / 30);
  if (diffDays <= 90) {
    return { text: `${months} ${months === 1 ? 'Monat' : 'Monate'}`, color: 'orange' };
  }
  return { text: `${months} ${months === 1 ? 'Monat' : 'Monate'}`, color: 'green' };
}

// Wie calculateRemainingTime, aber abgelaufene und noch nicht grüne Vorgänge
// werden als „In Prüfung" (gelb) dargestellt — die Frist ist um, läuft aber im
// BAFA-Portal noch.
export function getRemainingTimeBadge(endDate: string, status?: string): RemainingTime {
  const rt = calculateRemainingTime(endDate, status);
  if (rt.text === 'Abgelaufen' && !isGreenStatus(status)) {
    return { text: 'In Prüfung', color: 'yellow' };
  }
  return rt;
}

// Tailwind-Klassen für ein Restzeit-Badge je Farbe (inkl. Dark-Mode).
export function remainingBadgeClasses(color: RemainingColor): string {
  switch (color) {
    case 'neutral':
      return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300';
    case 'red':
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
    case 'orange':
      return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300';
    case 'yellow':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
    case 'green':
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
  }
}
